/**
 * activeOrganization.ts
 * -----------------------------------------------------------------------------
 * The organization this browser tab is acting in, and the one place the
 * `x-center-organization-id` header is built.
 *
 * The active organization is chosen by the client and validated by the server
 * against graph membership on every request (fastify-common
 * `resolveRequestedOrganizationId`); it is not a property of the Descope
 * session. It therefore lives in memory, per tab: two tabs on one session can
 * act in two organizations, and switching never touches the session.
 *
 * The request interceptor reads {@link getActiveOrganizationId} on every
 * request, so a switch is followed without rebuilding any client. Nothing here
 * reads shared storage (localStorage, cookies) on a request path; the
 * remembered selection is applied once, at initialization, by the host.
 */

/**
 * The transport for the active organization.
 *
 * Kept in lockstep with fastify-common's `ORGANIZATION_HEADER`
 * (`auth/resolveRequestedOrganizationId.ts`), which is the authority on the
 * name; `activeOrganization.spec.ts` pins the literal.
 */
export const ORGANIZATION_HEADER = 'x-center-organization-id';

type Listener = () => void;

let activeOrganizationId: string | null = null;
const listeners = new Set<Listener>();

/** The active organization id, or null in metasphere (own-identity) mode. */
export function getActiveOrganizationId(): string | null {
  return activeOrganizationId;
}

/**
 * Sets the active organization; null or an empty string selects metasphere
 * (own-identity) mode, in which no header is sent.
 */
export function setActiveOrganizationId(organizationId: string | null = null): void {
  const next = organizationId === '' ? null : organizationId;
  if (next === activeOrganizationId) return;
  activeOrganizationId = next;
  listeners.forEach((listener) => listener());
}

/** Subscribes to changes of the active organization; returns the unsubscribe function. */
export function subscribeActiveOrganization(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The organization header for one request: an empty object in metasphere mode,
 * so no header is put on the wire and only identity-level routes answer.
 */
export function activeOrganizationHeaders(): Record<string, string> {
  return activeOrganizationId ? { [ORGANIZATION_HEADER]: activeOrganizationId } : {};
}
